import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { VoiceCard } from '../common/voice-card';
import { VoiceAddButton } from './voice-add-button';
import { LoadingOverlay } from '../common/loading-overlay';

type VoiceItem = {
  id: string;
  sampleUrl: string;
  name: string;
  createdAt?: string;
};
interface TemplateVoiceModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  selectedId?: string;
  voices?: VoiceItem[];
  loading?: boolean;
  voiceTemplateLoading?: boolean;
  saveVoiceTemplateLoading?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave?: (voice: VoiceItem) => void;
  onRemoveVoiceTemplate?: (id: string) => void
  onSaveVoiceTemplate?: (voiceIds: string[]) => Promise<void>
}

/** 声音模板选择弹窗 */
export const TemplateVoiceModal: React.FC<TemplateVoiceModalProps> = ({
  trigger,
  open: externalOpen,
  selectedId: externalSelectedId,
  voices,
  loading,
  voiceTemplateLoading,
  saveVoiceTemplateLoading,
  onOpenChange,
  onSave,
  onRemoveVoiceTemplate,
  onSaveVoiceTemplate,
}) => {
  const { t } = useTranslation();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // 选中的声音列表
  const [selectedId, setSelectedId] = useState<string>(externalSelectedId || '');

  useEffect(() => {
    setSelectedId(externalSelectedId || '');
  }, [externalSelectedId]);

  // 处理单个声音卡片的选择变化
  const handleVoiceSelect = (id: string, isSelected: boolean) => {
    setSelectedId(id);
  };

  // 处理删除选中声音
  const handleRemove = (id: string) => {
    onRemoveVoiceTemplate?.(id);
  };

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  const handleSave = async () => {
    if (!selectedId) return;
    onSave?.(voices.find((item) => item.id === selectedId));
    handleClose();
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  // 处理滚动逻辑
  useEffect(() => {
    if (currentOpen && selectedId) {
      // 使用 setTimeout 确保 DOM 已经完全渲染并计算了位置
      const timer = setTimeout(() => {
        selectedItemRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',  // 滚动到容器中心位置
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentOpen, selectedId]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[900px] h-[755px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <span>{t('agent_creation.template_modal.voice_title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex flex-col px-6 pt-5 select-none mb-5 overflow-hidden'>
          <div className='relative pt-0.5 pl-0.5 pr-0.5 flex-1 flex flex-col h-full overflow-y-auto'>
            <div className='grid grid-cols-3 gap-x-3 gap-y-5'>
              <VoiceAddButton
                voiceIds={voices?.map((item) => item.id)}
                onSave={onSaveVoiceTemplate}
                saveLoading={saveVoiceTemplateLoading}
              />
              {voices?.map((item, index) => (
                <div
                  {...(selectedId === item.id ? { ref: selectedItemRef } : {})}
                >
                  <VoiceCard
                    key={`${item.id}_${index}`}
                    id={item.id}
                    name={item.name}
                    audioUrl={item.sampleUrl}
                    createdAt={item.createdAt}
                    enableSelect
                    isSelected={selectedId === item.id}
                    showEdit={false}
                    showDelete={false}
                    showRemove
                    onSelect={handleVoiceSelect}
                    onRemove={handleRemove}
                  />
                </div>
              ))}
            </div>
            <LoadingOverlay loading={voiceTemplateLoading} />
          </div>
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex items-center justify-end p-6 border-t border-[rgba(233,234,235,1)] z-20 select-none'>
          <div className='flex items-center gap-2'>
            <TextButton
              label={t('agent_dashboard.user_detail.voice.add_modal.cancel')}
              className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
              onClick={handleClose}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.voice.add_modal.save')}
              className='w-[135px] h-11'
              onClick={handleSave}
              loading={loading}
              disabled={loading || voiceTemplateLoading}
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
